import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Input, Space, Card, Tag, Row, Col, Select } from "antd";
import { Api_Url } from "../../../config/config";
import moment from "moment";
import axios from "axios";
import { BranchIssueModalAdd } from "./BranchIssueModalAdd";
import { BranchIssueModalResolve } from "./BranchIssueModalResolve";
import { ErrorToast } from "../../ToastComponent";

const { Option } = Select;

export const BranchIssue = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  // Modal States
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);

  // Filter States
  const [filters, setFilters] = useState({
    branch: "",
    status: null,
    searchTerm: "",
  });

  useEffect(() => {
    fetchBranches();
    fetchData();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(
        `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      const branchNames = response?.data?.data?.map((b) => b.name) || [];
      setBranches([...new Set(branchNames)]);
    } catch (error) {
      console.error("Failed to load branches:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetching all records (adjust PageSize as per your backend default)
      const response = await axios.get(
        `${Api_Url}api/v1/BranchIssue?PageNumber=1&PageSize=1000`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      setData(response?.data?.data || []);
      console.log(data)
    } catch (error) {
      ErrorToast("Failed to fetch branch issues");
    } finally {
      setLoading(false);
    }
  };

  // Memoized client-side filtering (mirrors IT Inventory logic)
  const filteredData = useMemo(() => {
    let filtered = [...data];
    if (filters.branch) {
      filtered = filtered.filter(
        (item) =>
          item.branchName?.toLowerCase() === filters.branch.toLowerCase()
      );
    }
    if (filters.status) {
      filtered = filtered.filter((item) => item.status === filters.status);
    }
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.issue?.toLowerCase().includes(term) ||
          item.id.toString().includes(term)
      );
    }
    return filtered;
  }, [data, filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ branch: "", status: null, searchTerm: "" });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      sorter: (a, b) => a.id - b.id,
      width: 80,
    },
    {
      title: "Branch",
      dataIndex: "branchName",
      sorter: (a, b) => a.branchName.localeCompare(b.branchName),
    },
    {
      title: "Colocation Center",
      dataIndex: "colocationCenter",
    },
    {
      title: "Issue Description",
      dataIndex: "issue",
      ellipsis: true,
    },
    {
      title: "Raised Date",
      dataIndex: "issueRaisedDate",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
    },
    {
      title: "Resolultion Description",
      dataIndex: "resolution",
      ellipsis: true,
    },
    {
      title: "Resolution Date",
      dataIndex: "resolutionDate",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "NA"),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "Resolved" ? "green" : "orange"}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Button
          variant="solid"
          disabled={record.status === "Resolved"}
          onClick={() => {
            setSelectedIssue(record);
            setIsResolveModalOpen(true);
          }}
        >
          {record.status === "Resolved" ? "Closed" : "Resolve Issue"}
        </Button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Header Section */}
        <div className="row mb-3">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Branch Issues Tracking</h3>
            <button
              className="btn btn-primary"
              onClick={() => setIsAddModalOpen(true)}
            >
              <i className="fa-solid fa-plus me-2"></i> Raise New Issue
            </button>
          </div>
        </div>

        {/* Filter Card */}
        <Card className="mb-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Search by Issue Description..."
                value={filters.searchTerm}
                onChange={(e) =>
                  handleFilterChange("searchTerm", e.target.value)
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Branch"
                value={filters.branch || undefined}
                onChange={(value) => handleFilterChange("branch", value)}
                allowClear
                showSearch
                style={{ width: "100%" }}
              >
                {branches.map((branch) => (
                  <Option key={branch} value={branch}>
                    {branch}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Status"
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
                allowClear
                style={{ width: "100%" }}
              >
                <Option value="Open">Open</Option>
                <Option value="Resolved">Resolved</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Button onClick={clearFilters} block>
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Table Section */}
        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            loading={loading}
            pagination={{
              showSizeChanger: true,
              defaultPageSize: 10,
              pageSizeOptions: ["10", "20", "50"],
            }}
            scroll={{ x: 1000 }}
          />
        </div>

        {/* Modals */}
        {isAddModalOpen && (
          <BranchIssueModalAdd
            onClose={() => setIsAddModalOpen(false)}
            onSuccess={() => {
              setIsAddModalOpen(false);
              fetchData();
            }}
          />
        )}

        {isResolveModalOpen && (
          <BranchIssueModalResolve
            issue={selectedIssue}
            onClose={() => setIsResolveModalOpen(false)}
            onSuccess={() => {
              setIsResolveModalOpen(false);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
};
